/// <reference types="@types/applepayjs" />

import type { ApplePayShippingAddress } from '@customink/project-service-react-components';
import {
  useApplePaySubmitNonce,
  useVerifyShippingAddress,
} from '@customink/project-service-react-components';
import {
  applePay as braintreeApplePay,
  client as braintreeClient,
} from 'braintree-web';
import type { ApplePay, ApplePayPayload } from 'braintree-web/apple-pay';
import { useEffect, useRef, useState } from 'react';

import { applePayAnalyticsEvent } from '@/components/vendors/googleAnalytics';
import { SUPPORTED_VERSION, isApplePaySupported } from '@/utils/applePayUtils';

type UseApplePaySessionOptions = {
  paymentToken: string;
  onError?: (error: string) => void;
  totalData?: { label: string; type: string; amount: string };
  pricingDetailsData?: Array<{ label: string; type: string; amount: string }>;
};

export function useApplePaySession({
  paymentToken,
  onError = () => undefined,
  totalData,
  pricingDetailsData,
}: UseApplePaySessionOptions) {
  const applePayInstanceRef = useRef<ApplePay | null>(null);
  const [isApplePayAvailable, setIsApplePayAvailable] =
    useState<boolean>(false);
  const [isSessionInProgress, setIsSessionInProgress] =
    useState<boolean>(false);
  const [hasActiveApplePayCards, setHasActiveApplePayCards] =
    useState<boolean>(true);
  const [verifyShippingAddress] = useVerifyShippingAddress();
  const [submitApplePayNonce] = useApplePaySubmitNonce();

  // Convert totalData to Apple Pay format
  const getTotal = (): ApplePayJS.ApplePayLineItem => {
    if (!totalData) {
      return { label: 'Total', amount: '0.00', type: 'final' };
    }
    return {
      label: totalData.label || 'Total',
      amount: totalData.amount || '0.00',
      type: totalData.type === 'pending' ? 'pending' : 'final',
    };
  };

  // Convert pricingDetailsData to Apple Pay format
  const getPriceDetails = (): ApplePayJS.ApplePayLineItem[] => {
    if (!pricingDetailsData) {
      return [];
    }
    return pricingDetailsData.map((item) => ({
      label: item.label || '',
      amount: item.amount || '0.00',
      type: item.type === 'pending' ? 'pending' : 'final',
    }));
  };

  // Initialize Apple Pay on component mount
  useEffect(() => {
    // Only proceed if ApplePaySession exists and payments are possible
    if (!isApplePaySupported()) return;

    initializeApplePay();
  }, [paymentToken, onError]);

  // Initialize Apple Pay with Braintree
  const initializeApplePay = async () => {
    if (!paymentToken) return;

    try {
      const client = await braintreeClient.create({
        authorization: paymentToken,
      });
      const applePay = await braintreeApplePay.create({ client });

      if (!applePay.merchantIdentifier) {
        onError?.('Apple Pay merchant identifier is missing.');
        return;
      }

      // The presence of merchantIdentifier and successful instance creation
      applePayInstanceRef.current = applePay;
      setIsApplePayAvailable(true);

      // Check for active cards during initialization
      try {
        const canMakePayments =
          await ApplePaySession.canMakePaymentsWithActiveCard(
            applePay.merchantIdentifier,
          );
        setHasActiveApplePayCards(canMakePayments);
      } catch (error) {
        console.error('Failed to check for active cards:', error);
        // Default to true to avoid breaking the experience if check fails
        setHasActiveApplePayCards(true);
      }
    } catch (err) {
      console.error('Failed to initialize Apple Pay:', err);
      onError?.('Failed to initialize Apple Pay.');
    }
  };

  // Format shipping contact from Apple Pay format to our API format
  const formatShippingContact = (
    shippingContact: ApplePayJS.ApplePayPaymentContact | null,
  ): ApplePayShippingAddress => {
    const addressLines = shippingContact?.addressLines || [];
    const address1 = addressLines[0] || '';
    const address2 = addressLines[1] || '';

    return {
      givenName: shippingContact?.givenName || '',
      familyName: shippingContact?.familyName || '',
      phone: shippingContact?.phoneNumber || '',
      address: {
        address1: address1,
        address2: address2,
        city: shippingContact?.locality || '',
        state: shippingContact?.administrativeArea || '',
        zip: shippingContact?.postalCode || '',
        country: shippingContact?.countryCode || '',
      },
    };
  };

  // Convert server error messages to Apple Pay format
  const convertServerErrors = (
    messages: Record<string, string[]>,
  ): ApplePayJS.ApplePayError[] => {
    if (!messages || Object.keys(messages).length === 0) {
      return [new ApplePayError('unknown', 'postalAddress', 'Unknown Error')];
    }

    const errors: ApplePayJS.ApplePayError[] = [];
    for (const field in messages) {
      if (messages[field] && messages[field].length > 0) {
        errors.push(
          new ApplePayError(
            'shippingContactInvalid',
            translateField(field) as ApplePayJS.ApplePayErrorContactField,
            messages[field][0],
          ),
        );
      }
    }
    return errors;
  };

  // Translate server field names to Apple Pay field names
  const translateField = (field: string): string => {
    const mapField: Record<string, string> = {
      'address.address1': 'addressLines',
      'address.address2': 'addressLines',
      'address.city': 'locality',
      'address.state': 'administrativeArea',
      'address.zip': 'postalCode',
      'address.country': 'countryCode',
      'contact.givenName': 'name',
      'contact.familyName': 'name',
      'contact.phone': 'phoneNumber',
      address_1: 'addressLines',
      address_2: 'addressLines',
      zip: 'postalCode',
      phone_number: 'phoneNumber',
      email: 'emailAddress',
      first_name: 'name',
      last_name: 'name',
    };
    return mapField[field] || 'postalAddress';
  };

  // Handle merchant validation
  const handleMerchantValidation = async (
    session: ApplePaySession,
    instance: ApplePay,
    event: ApplePayJS.ApplePayValidateMerchantEvent,
  ) => {
    try {
      const merchantSession = await instance.performValidation({
        validationURL: event.validationURL,
        displayName: 'CustomInk LLC',
      });
      session.completeMerchantValidation(merchantSession);
    } catch (e) {
      console.error('Merchant validation failed:', e);
      session.abort();
      onError?.('Merchant validation failed.');
      setIsSessionInProgress(false);
      applePayAnalyticsEvent('error merchant');
    }
  };

  // Handle shipping contact selection
  const handleShippingContactSelection = async (
    session: ApplePaySession,
    event: ApplePayJS.ApplePayShippingContactSelectedEvent,
  ) => {
    try {
      const shippingDetails = formatShippingContact(event.shippingContact);

      try {
        const result = await verifyShippingAddress({
          shipping: shippingDetails,
          fullValidation: false,
        }).unwrap();

        if (result.success) {
          const newTotal = result.newTotal || getTotal();
          const newItems = result.newItems || getPriceDetails();

          session.completeShippingContactSelection(
            ApplePaySession.STATUS_SUCCESS,
            [],
            newTotal,
            newItems,
          );
        } else {
          // Handle validation errors
          const messages = result.messages || {};
          const errors = convertServerErrors(messages);
          applePayAnalyticsEvent('error address');
          session.completeShippingContactSelection({
            newTotal: getTotal(),
            newLineItems: getPriceDetails(),
            errors: errors,
          });
        }
      } catch (apiError) {
        console.error('API error during shipping validation:', apiError);
        applePayAnalyticsEvent('error address');
        session.completeShippingContactSelection({
          newTotal: getTotal(),
          newLineItems: getPriceDetails(),
          errors: [
            new ApplePayError(
              'shippingContactInvalid',
              'postalAddress',
              'Failed to validate shipping address',
            ),
          ],
        });
      }
    } catch (e) {
      console.error('Failed to validate shipping contact:', e);
      applePayAnalyticsEvent('error address');
      session.completeShippingContactSelection({
        newTotal: getTotal(),
        newLineItems: getPriceDetails(),
        errors: [
          new ApplePayError(
            'shippingContactInvalid',
            'postalAddress',
            'Invalid shipping information',
          ),
        ],
      });
    }
  };

  // Handle payment authorization
  const handlePaymentAuthorization = async (
    session: ApplePaySession,
    instance: ApplePay,
    event: ApplePayJS.ApplePayPaymentAuthorizedEvent,
  ) => {
    try {
      // Tokenize the payment with Braintree
      const tokenizedPayload: ApplePayPayload = await instance.tokenize({
        token: event.payment.token,
      });

      const fullShippingDetails = formatShippingContact(
        event.payment.shippingContact || null,
      );

      try {
        // Verify the shipping address with full validation
        // Pass the combined fullShippingDetails object
        const verifyResult = await verifyShippingAddress({
          shipping: fullShippingDetails,
          fullValidation: true,
        }).unwrap();

        if (verifyResult.success) {
          // Submit the payment to the backend
          // Pass the combined fullShippingDetails object
          const submitResult = await submitApplePayNonce({
            payload: tokenizedPayload,
            shipping: fullShippingDetails,
          }).unwrap();

          if (submitResult.success) {
            session.completePayment(ApplePaySession.STATUS_SUCCESS);
            applePayAnalyticsEvent('success');

            // Redirect to confirmation page
            if (submitResult.url) {
              window.location.href = submitResult.url;
            }
          } else {
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            onError?.('Failed to process Apple Pay payment.');
            applePayAnalyticsEvent('failed payment');
          }
        } else {
          // Handle shipping validation errors
          const messages = verifyResult.messages || {};
          const errors = convertServerErrors(messages);
          applePayAnalyticsEvent('error address');
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
            errors: errors,
          });
        }
      } catch (apiError) {
        console.error('API error during payment processing:', apiError);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        onError?.('Failed to process payment due to API error.');
        applePayAnalyticsEvent('failed payment');
      }
    } catch (tokenizeErr) {
      console.error('Error tokenizing Apple Pay:', tokenizeErr);
      session.completePayment(ApplePaySession.STATUS_FAILURE);
      onError?.('Failed to process Apple Pay payment.');
      applePayAnalyticsEvent('failed payment');
    } finally {
      setIsSessionInProgress(false);
    }
  };

  // Begin the Apple Pay session
  const beginSession = () => {
    const instance = applePayInstanceRef.current;
    if (!instance) {
      onError?.('Apple Pay is not initialized.');
      return;
    }

    if (isSessionInProgress) {
      return;
    }

    setIsSessionInProgress(true);

    try {
      // Create payment request
      const paymentRequest = instance.createPaymentRequest({
        total: getTotal(),
        lineItems: getPriceDetails(),
        requiredShippingContactFields: ['postalAddress', 'name', 'phone'],
      });

      // Create Apple Pay session
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Type mismatch between Braintree and ApplePayJS
      const session = new ApplePaySession(
        SUPPORTED_VERSION,
        paymentRequest as ApplePayJS.ApplePayPaymentRequest,
      );

      // Set up event handlers
      session.onvalidatemerchant = (event) =>
        handleMerchantValidation(session, instance, event);
      session.onshippingcontactselected = (event) =>
        handleShippingContactSelection(session, event);
      session.onpaymentauthorized = (event) =>
        handlePaymentAuthorization(session, instance, event);

      session.oncancel = () => {
        setIsSessionInProgress(false);
        applePayAnalyticsEvent('cancel');
      };

      if (instance.merchantIdentifier) {
        ApplePaySession.canMakePaymentsWithActiveCard(
          instance.merchantIdentifier,
        )
          .then((canMakePayments) => {
            setHasActiveApplePayCards(canMakePayments);
            if (!canMakePayments) {
              onError?.(
                'No Apple Pay cards available. Please add a card to Apple Pay and try again.',
              );
              setIsSessionInProgress(false);
              applePayAnalyticsEvent('error empty card');
              return;
            }

            // Start the Apple Pay session
            session.begin();
          })
          .catch((error) => {
            console.error('Failed to check for active cards:', error);
            setIsSessionInProgress(false);
            onError?.('Failed to check for Apple Pay cards.');
          });
      } else {
        console.error('Merchant identifier is missing');
        setIsSessionInProgress(false);
        onError?.('Apple Pay configuration is incomplete.');
        applePayAnalyticsEvent('error merchant');
      }
    } catch (error) {
      console.error('Failed to begin Apple Pay session:', error);
      onError?.('Failed to begin Apple Pay session.');
      setIsSessionInProgress(false);
    }
  };

  return {
    beginSession,
    isApplePayAvailable,
    isSessionInProgress,
    hasActiveApplePayCards,
  };
}
